import { format } from 'date-fns';
import { toDate } from './dates';

export interface OverdueInvoice {
  invoiceNumber: string;
  loadNumber: string;
  /** The "PO number" chased with brokers — stored as the order number */
  poNumber: string;
  destination: string;
  /** When the invoice was raised; not always filled in */
  invoiceDate: Date | null;
  dueDate: Date;
}

const BLANK = 'N/A';

/**
 * Invoice and due dates are stored as plain `YYYY-MM-DD` strings from a date
 * input. Passing those to `new Date()` parses them as UTC midnight, which lands
 * on the previous day in US timezones and would report an invoice overdue a day
 * early — so the date-only form is built as a local date instead.
 */
export const parseDueDate = (value: any): Date | null => {
  if (typeof value === 'string') {
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const [, year, month, day] = match;
      const parsed = new Date(Number(year), Number(month) - 1, Number(day));
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    if (!value.trim()) return null;
  }
  return toDate(value);
};

const text = (value: any): string => {
  const asString = value === null || value === undefined ? '' : String(value).trim();
  return asString.length > 0 ? asString : BLANK;
};

/** Midnight today, so an invoice due today is not yet overdue. */
const startOfToday = (now: number): number => {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return today.getTime();
};

/**
 * Every invoice whose due date has passed, oldest due date first. Invoices with
 * no due date on record can't be judged, and PAID ones are left out.
 */
export const findOverdueInvoices = (invoices: any[], now: number = Date.now()): OverdueInvoice[] => {
  const cutoff = startOfToday(now);

  return invoices
    .map(invoice => ({ invoice, dueDate: parseDueDate(invoice.dueDate) }))
    .filter(({ invoice, dueDate }) => {
      if (!dueDate) return false;
      if (invoice.status === 'PAID') return false;
      return dueDate.getTime() < cutoff;
    })
    .sort((a, b) => a.dueDate!.getTime() - b.dueDate!.getTime())
    .map(({ invoice, dueDate }) => ({
      invoiceNumber: text(invoice.invoiceNumber),
      loadNumber: text(invoice.loadNumber),
      poNumber: text(invoice.orderNumber),
      destination: text(invoice.destination),
      invoiceDate: parseDueDate(invoice.invoiceDate),
      dueDate: dueDate as Date
    }));
};

/**
 * A plain-text block meant to be pasted straight into an email: a short header
 * line, then one numbered line per load, oldest due date first.
 */
export const formatOverdueInvoices = (
  overdue: OverdueInvoice[],
  now: number = Date.now(),
  /** Set when a single broker is selected, so the pasted list says whose it is */
  brokerLabel?: string
): string => {
  const forBroker = brokerLabel ? ` for ${brokerLabel}` : '';
  const header = `Overdue invoices${forBroker} as of ${format(
    new Date(now),
    'MM/dd/yyyy'
  )} (${overdue.length} ${overdue.length === 1 ? 'load' : 'loads'})`;

  if (overdue.length === 0) {
    return `${header}\n\nNo invoices are past their due date.`;
  }

  const lines = overdue.map(
    (item, index) =>
      `${index + 1}. Invoice ${item.invoiceNumber} | Load ${item.loadNumber} | PO ${item.poNumber} | ${
        item.destination
      } | Invoiced ${item.invoiceDate ? format(item.invoiceDate, 'MM/dd/yyyy') : BLANK} | Due ${format(
        item.dueDate,
        'MM/dd/yyyy'
      )}`
  );

  return `${header}\n\n${lines.join('\n')}`;
};
